// Tremor Checkbox [v1.0.0]

import React from "react"
import { tv, type VariantProps } from "tailwind-variants"

import { cx, focusRing } from "@/lib/utils"

const checkboxVariants = tv({
    base: [
        // base
        "appearance-none",
        "relative inline-flex items-center justify-center",
        "h-4 w-4 rounded border shadow-xs transition-all duration-100 ease-in-out",
        "cursor-pointer",
        // border color
        "border-gray-300",
        // background color
        "bg-white",
        // hover
        "hover:border-[#079a95]",
        // checked state
        "checked:border-[#079a95] checked:bg-[#079a95]",
        "checked:bg-[url('data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%20010%201.414l-5%205a1%201%200%2001-1.414%200l-2-2a1%201%200%20011.414-1.414L6.5%209.086l4.293-4.293a1%201%200%20011.414%200z%22%2F%3E%3C%2Fsvg%3E')]",
        "checked:bg-no-repeat checked:bg-center checked:bg-[length:12px_12px]",
        // disabled
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:border-gray-300 disabled:bg-gray-100",
        // focus
        "focus:ring-2 focus:ring-[#079a95]/20 focus:border-[#079a95]",
        focusRing,
    ],
    variants: {
        size: {
            sm: "h-3 w-3",
            md: "h-4 w-4",
            lg: "h-5 w-5",
        },
        hasError: {
            true: [
                "border-red-500",
                "focus:border-red-500 focus:ring-red-500/20",
                "checked:border-red-500 checked:bg-red-500",
            ],
        },
    },
    defaultVariants: {
        size: "md",
    },
})

interface CheckboxProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof checkboxVariants> {
    label?: string | React.ReactNode
    description?: string | React.ReactNode
    hasError?: boolean;
    labelTrigger?: boolean;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            className,
            size,
            hasError,
            label,
            description,
            id,
            disabled,
            labelTrigger = false,
            ...props
        }: CheckboxProps,
        forwardedRef,
    ) => {
        const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`

        return (
            <div className="flex items-start gap-2" tremor-id="tremor-raw">
                <input
                    ref={forwardedRef}
                    type="checkbox"
                    id={checkboxId}
                    className={cx(checkboxVariants({ size, hasError }), className)}
                    disabled={disabled}
                    {...props}
                />
                {(label || description) && (
                    <div className="flex flex-col">
                        {label && (
                            <label
                                htmlFor={labelTrigger ? checkboxId : undefined}
                                className={cx(
                                    "text-sm font-medium m-[-2px] text-gray-900 cursor-pointer",
                                    {
                                        "text-gray-400 cursor-not-allowed": disabled,
                                        "text-red-600": hasError,
                                    }
                                )}
                            >
                                {label}
                            </label>
                        )}
                        {description && (
                            <p
                                className={cx(
                                    "text-xs text-gray-500",
                                    {
                                        "mt-0.5": label,
                                    },
                                    {
                                        "text-gray-400": disabled,
                                        "text-red-500": hasError,
                                    }
                                )}
                            >
                                {description}
                            </p>
                        )}
                    </div>
                )}
            </div>
        )
    },
)

Checkbox.displayName = "Checkbox"

export { Checkbox, checkboxVariants, type CheckboxProps }